import { useCallback, useRef, useState } from "react";
import { useSpatial } from "../components/SpatialContext";
import { deskDocContains, resolveDeskTarget, findDeskUpdate, sameDeskText } from "../lib/deskWrites";

const VERIFY_TIMEOUT_MS = 1500;
const VERIFY_POLL_MS = 50;

/**
 * Lumen → desk actions. Every write ends in a receipt that says what really landed:
 * the doc is read back until the content is there, then saved. A write that needs the
 * user's choice returns a "pending" receipt that is swapped for the real result once they pick.
 *
 * Two kinds of question:
 * - "version": Lumen wrote a whole new version of an existing doc → Replace / Keep both.
 * - "placement": Lumen asked where a piece of content should go → Add to existing / New document.
 */
export function useDeskActions({ setActiveHotspot, setLumenMessages }) {
  const { addSurfaceItem, readDeskDocument, flushWorldJson, currentLocationRef, surfaceState } = useSpatial();
  const [pendingDeskAction, setPendingDeskActionState] = useState(null);
  const [focusDeskDoc, setFocusDeskDoc] = useState(null);
  const pendingRef = useRef(null);
  // The last "Replace" — one step of undo, only while the doc still holds exactly what was replaced in.
  const [undoableReplaceId, setUndoableReplaceId] = useState(null);
  const lastReplaceRef = useRef(null);

  const setPending = (next) => {
    pendingRef.current = next;
    setPendingDeskActionState(next);
  };

  /** Replaces the receipt on the reply that asked, wherever that reply lives. */
  const settleReceipt = useCallback((target, receipt) => {
    if (!target) return;
    if (target.onSettled) {
      target.onSettled(target.id, receipt);
      return;
    }
    setLumenMessages?.((prev) => {
      const idx = prev.findIndex((m) => m.deskReceipt?.pendingId === target.id);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...prev[idx], deskReceipt: receipt };
      return next;
    });
  }, [setLumenMessages]);

  /** A newer question replaces an unanswered one — the older reply must not keep saying "waiting". */
  const openPending = (pending) => {
    const previous = pendingRef.current;
    if (previous) settleReceipt(previous, { status: "failed", reason: "choice_closed", title: previous.existingTitle || null });
    setPending(pending);
    setActiveHotspot?.("desk");
    return { status: "pending", pendingId: pending.id, title: pending.existingTitle || null };
  };

  const confirmDeskWrite = async (result, content) => {
    if (!result?.ok) {
      return { status: "failed", reason: result?.reason || "unknown", title: result?.title || null, docId: result?.docId || null };
    }
    const deadline = Date.now() + VERIFY_TIMEOUT_MS;
    let doc = null;
    for (;;) {
      doc = readDeskDocument(result.docId);
      if (doc && deskDocContains(doc.content, content, doc.title)) break;
      doc = null;
      if (Date.now() >= deadline) break;
      await new Promise((r) => setTimeout(r, VERIFY_POLL_MS));
    }
    if (!doc) return { status: "failed", reason: "verify_failed", title: result.title, docId: result.docId };

    let saved = false;
    try {
      saved = (await flushWorldJson())?.ok !== false;
    } catch {
      saved = false;
    }
    return { status: result.status, title: doc.title || result.title, docId: result.docId, saved };
  };

  /** Only the fields Lumen is allowed to set — a stray id would turn a create into an overwrite. */
  const lumenDeskUpdate = (update) => ({
    surface: "desk",
    type: update.type,
    title: update.title,
    content: update.content,
    doc_id: update.doc_id,
  });

  /**
   * action: "new_doc" | "append" | "ask_user". options.onPendingSettled(pendingId, receipt) is for
   * chats that keep their own messages (focus mode); the main Lumen chat is settled here.
   */
  const handleDeskAction = async (action, updates, options = {}) => {
    const raw = findDeskUpdate(updates);
    if (!raw) return { status: "failed", reason: "no_desk_update" };
    const deskUpdate = lumenDeskUpdate(raw);
    const roomId = currentLocationRef.current?.roomId;
    if (!roomId) return { status: "failed", reason: "no_room" };
    const newPending = (extra) => ({
      id: `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      deskUpdate,
      roomId,
      onSettled: options.onPendingSettled || null,
      ...extra,
    });

    if (action === "ask_user") {
      const deskDocs = (surfaceState?.deskDocuments || []).filter((d) => (d.roomId || d.room_id) === roomId);
      const target = resolveDeskTarget(deskDocs, deskUpdate) || deskDocs[0] || null;
      if (!target) action = "new_doc";
      else return openPending(newPending({ kind: "placement", targetDocId: target.id, existingTitle: target.title, currentContent: target.content || "" }));
    }
    if (action !== "new_doc" && action !== "append") return { status: "failed", reason: "unknown_action" };

    const result = addSurfaceItem({
      ...deskUpdate,
      room_id: roomId,
      user_edited: true,
      forceNew: action === "new_doc",
      append: action === "append",
      // Lumen never overwrites or duplicates on its own — the user picks.
      onTitleConflict: "reject",
    });
    if (result?.status === "conflict") {
      // Both kinds of conflict carry a whole version of the doc: a same-title new doc, or an
      // append that mostly repeats it.
      return openPending(newPending({ kind: "version", targetDocId: result.docId, existingTitle: result.title, currentContent: readDeskDocument(result.docId)?.content || "" }));
    }
    if (result?.ok) {
      setFocusDeskDoc({ docId: result.docId, at: Date.now() });
      setActiveHotspot?.("desk");
    }
    return confirmDeskWrite(result, deskUpdate.content);
  };

  /** choice: "replace" | "new" | "append" | "dismiss". Returns the receipt for the desk's own toast. */
  const resolvePendingDeskAction = async (choice) => {
    const pending = pendingRef.current;
    setPending(null);
    if (!pending?.deskUpdate) return null;
    if (choice === "dismiss" || currentLocationRef.current?.roomId !== pending.roomId) {
      const closed = { status: "failed", reason: choice === "dismiss" ? "dismissed" : "choice_closed", title: pending.existingTitle || null };
      settleReceipt(pending, closed);
      return closed;
    }
    const base = { ...pending.deskUpdate, room_id: pending.roomId, user_edited: true };
    const target = { doc_id: pending.targetDocId, title: pending.existingTitle || base.title };
    let result;
    if (choice === "replace") result = addSurfaceItem({ ...base, ...target, replace: true });
    else if (choice === "append") result = addSurfaceItem({ ...base, ...target, append: true, forceAppend: true });
    else result = addSurfaceItem({ ...base, forceNew: true });

    if (result?.ok) setFocusDeskDoc({ docId: result.docId, at: Date.now() });
    const receipt = await confirmDeskWrite(result, pending.deskUpdate.content);
    if (choice === "new" && pending.kind === "version" && receipt.status === "created") receipt.keptBoth = true;
    if (receipt.status === "replaced" && result.previousContent != null) {
      receipt.pendingId = pending.id;
      lastReplaceRef.current = {
        target: pending,
        docId: result.docId,
        roomId: pending.roomId,
        title: receipt.title,
        previousContent: result.previousContent,
        replacedContent: pending.deskUpdate.content,
      };
      setUndoableReplaceId(pending.id);
    }
    settleReceipt(pending, receipt);
    return receipt;
  };

  /**
   * Puts the old version back after a "Replace". Refused if the doc was edited since, so undo
   * can never throw away the user's own changes. Returns { ok, receipt?, reason? }.
   */
  const undoDeskReplace = async () => {
    const last = lastReplaceRef.current;
    lastReplaceRef.current = null;
    setUndoableReplaceId(null);
    if (!last) return { ok: false, reason: "nothing_to_undo" };
    const doc = readDeskDocument(last.docId);
    if (!doc || currentLocationRef.current?.roomId !== last.roomId || !sameDeskText(doc.content, last.replacedContent)) {
      return { ok: false, reason: "changed_since" };
    }
    const result = addSurfaceItem({ surface: "desk", doc_id: last.docId, title: last.title, content: last.previousContent, room_id: last.roomId, replace: true, user_edited: true });
    const check = await confirmDeskWrite(result, last.previousContent);
    if (!["replaced", "unchanged"].includes(check.status)) return { ok: false, reason: check.reason || "verify_failed" };
    const receipt = { status: "undone", title: last.title, docId: last.docId };
    settleReceipt(last.target, receipt);
    return { ok: true, receipt };
  };

  /**
   * Closing the desk or leaving the room ends the question without adding anything.
   * On a room change the main chat is already swapping to the next room's messages, so its
   * receipt is left for settleStaleDeskReceipts when that conversation loads again.
   */
  const closePendingDeskAction = ({ leavingRoom = false } = {}) => {
    if (leavingRoom) {
      lastReplaceRef.current = null;
      setUndoableReplaceId(null);
    }
    const pending = pendingRef.current;
    if (!pending) return;
    setPending(null);
    if (leavingRoom && !pending.onSettled) return;
    settleReceipt(pending, { status: "failed", reason: "choice_closed", title: pending.existingTitle || null });
  };

  /** Everything a chat or the desk needs to show and answer the open question. */
  const deskChoices = {
    pending: pendingDeskAction,
    resolve: resolvePendingDeskAction,
    undoableReplaceId,
    undoReplace: undoDeskReplace,
  };

  return {
    pendingDeskAction,
    focusDeskDoc,
    setFocusDeskDoc,
    handleDeskAction,
    resolvePendingDeskAction,
    closePendingDeskAction,
    undoDeskReplace,
    deskChoices,
  };
}
